import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";
import {
  ChevronRight,
  Inbox,
  DollarSign,
  ShieldCheck,
  Clock,
  Eye,
  CheckCircle2,
  Store,
} from "lucide-react";

/**
 * Partner / "For Shops" marketing page.
 * Distinct from the homepage teaser — this is the SEO-target landing page
 * shops arrive on from sales emails, footer links, and direct nav.
 * No invented shop counts, no fake commission %.
 */
export default function ForShopsPage() {
  const navigate = useNavigate();

  const faq = [
    {
      q: "How do requests reach my shop?",
      a: "Drivers submit a request from your service area (their chosen radius). Matching shops receive it in their dashboard and by email — no cold calls.",
    },
    {
      q: "Do competitors see my quote?",
      a: "No. Quotes are private per shop. Rival shops can't see your price, your notes, or the customer's contact details.",
    },
    {
      q: "How fast should I reply?",
      a: "Aim to respond within 24 hours (ideally under 2 hours on weekdays). Faster replies typically win more work.",
    },
    {
      q: "How does commission work?",
      a: "Commission applies to jobs completed through TyreMatch and is confirmed at approval — no surprise fees. Exact terms are provided in your partner agreement.",
    },
    {
      q: "Do I need to change my pricing?",
      a: "No. You set your own quote per request. TyreMatch never sets the retail price for your shop.",
    },
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://tyrematch.co.nz/" },
      { "@type": "ListItem", position: 2, name: "For Tyre Shops", item: "https://tyrematch.co.nz/for-shops" },
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO page="forShops" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-heading font-bold text-lg text-primary" data-testid="fs-home-link">
            TyreMatch
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/how-it-works" className="text-muted-foreground hover:text-primary">How it works</Link>
            <Button
              size="sm"
              onClick={() => navigate("/shop/auth?tab=register")}
              data-testid="fs-cta-register-btn"
            >
              Register your shop
            </Button>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-10">
        <div>
          <p className="text-sm text-primary font-semibold uppercase tracking-wider mb-2 flex items-center gap-2">
            <Store className="h-4 w-4" /> For NZ tyre shops
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold leading-tight mb-4">
            Inbound quote requests from drivers in your area
          </h1>
          <p className="text-lg text-muted-foreground">
            Drivers request tyre and wheel-service quotes on TyreMatch. Matching shops in the driver's radius receive the
            request privately — you set your own price, competitors can't see your quote, and there's no cold calling.
          </p>
          <div className="mt-5">
            <Button
              size="lg"
              onClick={() => navigate("/shop/auth?tab=register")}
              className="rounded-full"
              data-testid="fs-hero-cta"
            >
              Register your shop
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Value props */}
        <section className="grid sm:grid-cols-2 gap-4">
          {[
            { icon: Inbox, title: "Inbound requests only", body: "Drivers come to you with a request — no scripted outbound calls." },
            { icon: Eye, title: "Private quotes", body: "Rival shops never see your price, your notes, or the customer's details." },
            { icon: DollarSign, title: "You set the price", body: "Every quote is yours to price. TyreMatch never sets retail." },
            { icon: Clock, title: "Aim under 24 hours", body: "Faster responses (ideally <2h on weekdays) tend to win more work." },
            { icon: ShieldCheck, title: "Commission on completion", body: "Commission applies to jobs completed through TyreMatch and is confirmed at approval." },
            { icon: CheckCircle2, title: "Build a review record", body: "Real customer reviews build your reputation over time." },
          ].map((v, i) => (
            <div key={i} className="rounded-xl border p-4 bg-card">
              <v.icon className="h-6 w-6 text-primary mb-2" />
              <h3 className="font-semibold mb-1">{v.title}</h3>
              <p className="text-sm text-muted-foreground">{v.body}</p>
            </div>
          ))}
        </section>

        {/* How it works — from a shop's POV */}
        <section>
          <h2 className="font-heading text-2xl font-bold mb-3">How it works for shops</h2>
          <ol className="list-decimal ml-5 space-y-2 text-muted-foreground">
            <li>Register your shop and confirm your service area on the map.</li>
            <li>Matching driver requests land in your dashboard (and by email).</li>
            <li>Submit a private quote — you set the price and can include options.</li>
            <li>If the customer accepts, they book fitment with you. Payment happens at the shop.</li>
            <li>Mark the job complete and your review request goes out automatically.</li>
          </ol>
        </section>

        {/* Marketplace disclosure */}
        <section className="rounded-xl border-l-4 border-primary bg-primary/5 p-4 text-sm">
          <strong>TyreMatch is a marketplace, not a reseller.</strong> Fitment payment goes directly from the driver to your
          shop — TyreMatch is not the tyre seller or the payment processor for fitment.
        </section>

        {/* FAQ */}
        <section>
          <h2 className="font-heading text-2xl font-bold mb-3">FAQ</h2>
          <div className="space-y-3">
            {faq.map((f, i) => (
              <details key={i} className="rounded-lg border px-3 py-2 bg-card">
                <summary className="cursor-pointer font-medium py-1">{f.q}</summary>
                <p className="text-sm text-muted-foreground mt-2">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="rounded-2xl bg-primary text-primary-foreground p-6 text-center">
          <h2 className="font-heading text-2xl font-bold mb-2">Ready to join TyreMatch?</h2>
          <p className="text-primary-foreground/85 mb-4 text-sm">Set up your shop profile in a few minutes.</p>
          <Button
            size="lg"
            onClick={() => navigate("/shop/auth?tab=register")}
            className="bg-background text-foreground hover:bg-background/90 rounded-full"
            data-testid="fs-closing-cta"
          >
            Register your shop
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
          <p className="text-primary-foreground/70 text-xs mt-3">
            Already a partner?{" "}
            <Link to="/shop/auth?tab=login" className="underline">Shop login</Link>
          </p>
        </section>
      </main>

      <footer className="border-t py-6 text-center text-xs text-muted-foreground">
        <div className="max-w-3xl mx-auto px-4">
          TyreMatch is a marketplace connecting NZ drivers with local tyre shops.
        </div>
      </footer>
    </div>
  );
}
